import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import type { ReactNode } from "react";
import { useLocation } from "react-router-dom";
import { SCREEN_LABELS, screenLabel } from "@/lib/screen-labels";
import type { Location } from "react-router-dom";

interface RouteSnapshot {
  pathname: string;
  search: string;
}

interface NavigationHistoryContextValue {
  previousRoute: RouteSnapshot | null;
  previousLabel: string | null;
}

type NavigationHistoryMap = Record<number, RouteSnapshot>;

const STORAGE_KEY = "portal-navigation-history";

const NavigationHistoryContext = createContext<
  NavigationHistoryContextValue | undefined
>(undefined);

function snapshotLocation(location: Location): RouteSnapshot {
  return {
    pathname: location.pathname,
    search: location.search,
  };
}

function getBrowserHistoryIndex(): number | null {
  const index = window.history.state?.idx;
  return typeof index === "number" ? index : null;
}

function readStoredHistory(): NavigationHistoryMap {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    return raw ? (JSON.parse(raw) as NavigationHistoryMap) : {};
  } catch {
    return {};
  }
}

function writeStoredHistory(history: NavigationHistoryMap) {
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(history));
  } catch {
    // Le libellé de retour reste disponible en mémoire même si le stockage échoue.
  }
}

/** Singular labels for detail screens; list screens come from SCREEN_LABELS. */
const DETAIL_LABELS: ReadonlyArray<[RegExp, string]> = [
  [/^\/commandes\/\d+/, "Commande"],
  [/^\/clients\/contacts\/\d+/, "Contact"],
  [/^\/clients\/\d+/, "Client"],
  [/^\/abonnements\/\d+/, "Abonnement"],
  [/^\/evenements\/.*\/tasks\/\d+/, "Tâche évènement"],
  [/^\/evenements\/tasks\/\d+/, "Tâche évènement"],
  [/^\/evenements\/\d+/, "Évènement"],
  [/^\/preparations\/\d+/, "Tâche"],
  [/^\/medias\/\d+/, "Photo"],
];

function getRouteLabel(pathname: string): string {
  for (const [pattern, label] of DETAIL_LABELS) {
    if (pattern.test(pathname)) return label;
  }
  return screenLabel(pathname) ?? "Page précédente";
}

/** Where a detail screen goes back to when there is no history to read. */
const DETAIL_PARENTS: ReadonlyArray<[RegExp, string]> = [
  [/^\/commandes\/\d+/, "/commandes"],
  [/^\/clients\/contacts\/\d+/, "/clients"],
  [/^\/clients\/\d+/, "/clients"],
  [/^\/abonnements\/\d+/, "/abonnements"],
  [/^\/evenements\/.*\/tasks\/\d+/, "/evenements"],
  [/^\/evenements\/tasks\/\d+/, "/evenements"],
  [/^\/evenements\/\d+/, "/evenements"],
  [/^\/preparations\/\d+/, "/mes-taches"],
  [/^\/medias\/\d+/, "/medias"],
];

export function getFallbackBackLabel(pathname: string): string {
  for (const [pattern, parent] of DETAIL_PARENTS) {
    if (pattern.test(pathname)) return SCREEN_LABELS[parent] ?? "Accueil";
  }
  return "Accueil";
}

export function NavigationHistoryProvider({ children }: { children: ReactNode }) {
  const location = useLocation();
  const [historyMap, setHistoryMap] =
    useState<NavigationHistoryMap>(readStoredHistory);
  const [currentIndex, setCurrentIndex] = useState<number | null>(
    getBrowserHistoryIndex,
  );

  useEffect(() => {
    const index = getBrowserHistoryIndex();
    if (index === null) return;

    const nextRoute = snapshotLocation(location);
    setCurrentIndex(index);
    setHistoryMap((previousHistory) => {
      const nextHistory = {
        ...previousHistory,
        [index]: nextRoute,
      };
      writeStoredHistory(nextHistory);
      return nextHistory;
    });
  }, [location.pathname, location.search]);

  const previousRoute =
    currentIndex !== null ? historyMap[currentIndex - 1] ?? null : null;

  const value = useMemo(
    () => ({
      previousRoute,
      previousLabel: previousRoute ? getRouteLabel(previousRoute.pathname) : null,
    }),
    [previousRoute],
  );

  return (
    <NavigationHistoryContext.Provider value={value}>
      {children}
    </NavigationHistoryContext.Provider>
  );
}

export function useNavigationHistory() {
  const context = useContext(NavigationHistoryContext);
  if (!context) {
    throw new Error(
      "useNavigationHistory doit être utilisé dans NavigationHistoryProvider",
    );
  }
  return context;
}
